import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription, switchMap } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Oauth2Service } from '../oauth2.service';
import { FlowComponent } from '../controls/flow/flow.component';
import { LogoutComponent } from '../controls/logout/logout.component';

import { navigateToUrl } from '../utils';
import { AcceptOAuth2ConsentRequestSession, OAuth2ConsentRequest } from '../../../sdk/hydra';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UIToggle } from '@irohalab/deneb-ui';
import { AuthService } from '../auth.service';
import { Session } from '../../../sdk/kratos';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';

const ONE_MONTH_IN_SECONDS = 3600 * 24 * 30;

@Component({
    selector: 'app-consent',
    imports: [
    UIToggle,
    ReactiveFormsModule,
    ThemeToggleComponent
],
    templateUrl: './consent.component.html',
    styleUrl: './consent.component.less',
    providers: [{ provide: Oauth2Service, useClass: Oauth2Service }]
})
export class ConsentComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  uiState: 'showConsent' | 'silent' | 'error' = 'silent';
  uiError!: string;

  consentRequest!: OAuth2ConsentRequest;
  consentForm!: FormGroup;
  session!: Session;

  constructor(private oauth2Service: Oauth2Service,
              private activatedRoute: ActivatedRoute,
              private authService: AuthService,
              private router: Router,
              private formBuilder: FormBuilder) {
  }

  ngOnDestroy(): void {
      this.subscription.unsubscribe();
  }
  ngOnInit(): void {
    const paramMap = this.activatedRoute.snapshot.queryParamMap;
    const consentChallenge = paramMap.get('consent_challenge') || undefined;
    if (!consentChallenge) {
      this.uiState = 'error';
      this.uiError = 'Consent challenge is missing';
    } else {
      this.subscription.add(
        forkJoin({
          session: this.authService.checkCurrentSession(),
          consentRequest: this.oauth2Service.getConsent(consentChallenge)
        })
          .subscribe({
            next: ({session, consentRequest}) => {
              this.consentForm = this.formBuilder.group({
                remember: [false]
              });
              this.consentRequest = consentRequest;
              this.session = session;
              this.uiState = 'showConsent';
            },
            error: (response: any) => {
              if (response.status && response.status === 410) {
                const redirectTo = response.error.redirect_to;
                navigateToUrl(this.router, redirectTo || '/').then(() => {});
              } else {
                this.uiError = response.message || 'Unknown error';
                this.uiState = 'error';
              }
            }
          })
      );
    }
  }

  accept(): void {
    const grantScope = this.consentRequest.requested_scope;
    const consentRequestSession: AcceptOAuth2ConsentRequestSession = {
      access_token: {},
      id_token: {}
    };
    const identity = this.session.identity;
    if (!identity) {
      // should not be here.
      this.uiState = 'error';
      this.uiError = 'Session not available';
    }
    if (grantScope?.includes('email')) {
      const addresses = identity?.verifiable_addresses || []
      if (addresses.length > 0) {
        const address = addresses[0];
        if (address.via === 'email') {
          consentRequestSession.id_token.email = address.value;
          consentRequestSession.id_token.email_verified = address.verified;
        }
      }
    }

    if (grantScope?.includes('profile')) {
      if (identity?.traits.name) {
        consentRequestSession.id_token.name = identity.traits.name;
      }

      if (identity?.updated_at) {
        consentRequestSession.id_token.updated_at = parseInt((Date.parse(identity.updated_at) / 1000).toFixed(0), 10);
      }
    }
    this.subscription.add(
      this.oauth2Service.acceptConsent({
        context: this.consentRequest.context,
        grant_access_token_audience: this.consentRequest.requested_access_token_audience,
        grant_scope: grantScope,
        // handled_at: new Date().toUTCString(),
        remember: this.consentForm.value.remember,
        remember_for: ONE_MONTH_IN_SECONDS,
        session: consentRequestSession
      }, this.consentRequest.challenge)
        .pipe(switchMap((redirectTo) => {
          return navigateToUrl(this.router, redirectTo);
        }))
        .subscribe({
          next: () => {},
          error: (err) => {
            console.log(err);
            this.uiError = err.error;
            this.uiState = 'error';
          }
        })
    );
  }

  reject(): void {
    this.subscription.add(
      this.oauth2Service.rejectConsent(this.consentRequest.challenge, {
        error: 'access_denied',
        error_debug: 'user_reject_access',
        error_description: 'The resource owner denied the request'
      })
        .subscribe({
          next: (redirectTo) => {
            navigateToUrl(this.router, redirectTo).then(() => {});
          },
          error: (err) => {
            this.uiState = 'error';
            this.uiError = err.error;
          }
        })
    );
  }
}
